import { useEffect, useState, useRef } from "react";
import { produce } from "immer";
import isEqual from "react-fast-compare";
import {
  getOrderedFilters,
  useFilters,
} from "../../../Pages/DataManagement/DataExplorer/hooks/useExplorerFilters/useFilters";
import { isEqualWith, omit, set, keyBy } from "lodash-es";
import { showToastWithTimeout } from "../../../store/actions/message";
import { useForm } from "react-hook-form";
import useAdaptHookFormToState from "../../../hooks/useAdaptHookFormToState";
import { visibilityTypes } from "../../../utils/activeTable";

/**
 * @typedef {import('@ag-grid-community/core').ColDef} ColDef
 */

/**
 * @typedef {Object} VisibleField
 * @property {string} colId
 * @property {string} [aggregationType]
 **/

const defaultInitialState = { visibleFields: [], filters: [] };
export default function useActiveTableViewManager({
  activeViewInitialState = defaultInitialState,
  allColumns,
  handleSaveView,
  user,
}) {
  activeViewInitialState = produce(activeViewInitialState, (draft) => {
    if (!draft.displaySettings) {
      draft.displaySettings = {};
    }
    if (!draft.displaySettings.orders) {
      // Ensure that orders is an array.
      draft.displaySettings.orders = [];
    }
  });

  const previousViewInitialStateRef = useRef(null);
  const viewForm = useForm({ defaultValues: activeViewInitialState });
  const [nextView, setNextView] = useAdaptHookFormToState(viewForm);
  const [nextColumns, setNextColumns] = useState(allColumns); // all columns with views settings
  const [name, setName] = useState(activeViewInitialState?.name || "");
  // filters only used on create view screen for copy template from another view
  const [filters, setFilters] = useState([]);

  const [processing, setProcessing] = useState(false);
  const filterApi = useFilters(activeViewInitialState.filters ?? []);

  useEffect(() => {
    if (
      !previousViewInitialStateRef.current ||
      !isEqual(previousViewInitialStateRef.current, activeViewInitialState)
    ) {
      previousViewInitialStateRef.current = activeViewInitialState;
      const nextMergedColumns = convertedColumns(
        activeViewInitialState,
        allColumns
      );
      setNextColumns(nextMergedColumns);
      setNextView(activeViewInitialState);
      setName(activeViewInitialState?.name || "");
      filterApi.setDraftFilters(
        getOrderedFilters(activeViewInitialState?.filters || [])
      );
    }
  }, [activeViewInitialState, allColumns, filterApi, setNextView]);

  function changeValue(columnUuid, property, value) {
    return setNextColumns((nextColumns) =>
      produce(nextColumns, (draft) => {
        const columnIndex = nextColumns.findIndex(
          (c) => c.colId === columnUuid
        );
        if (columnIndex < 0) {
          return;
        }
        set(draft[columnIndex], property, value);
      })
    );
  }

  function setVisible(columnUuid) {
    changeValue(columnUuid, "visible", true);
  }

  function setInvisible(columnUuid) {
    changeValue(columnUuid, "visible", false);
  }

  function setAllVisible() {
    const updatedColumns = nextColumns.map((c) => ({ ...c, visible: true }));
    return setNextColumns(updatedColumns);
  }

  function setAllInvisible() {
    const updatedColumns = nextColumns.map((c) => ({ ...c, visible: false }));
    return setNextColumns(updatedColumns);
  }

  const visibleColumns = nextColumns.filter((c) => c.visible);

  const nextViewSettings = {
    ...nextView,
    visibleFields: visibleColumns,
    name,
    filters: filterApi.draftFilters.length ? filterApi.draftFilters : filters,
  };

  const disabledReason = processing
    ? "The form is currently being saved"
    : !name
    ? "Please provide a name."
    : null;

  const convertedActiveViewInitialState = {
    ...activeViewInitialState,
    visibleFields: convertedColumns(activeViewInitialState, allColumns).filter(
      (c) => c.visible
    ),
    filters: activeViewInitialState?.filters ?? [],
  };

  const isDirty = !isEqualWith(
    // updatedAt will change afte api call so we do not need to compare it
    omit(convertedActiveViewInitialState, "updatedAt"),
    omit(nextViewSettings, "updatedAt"),
    isEqualComparator
  );

  async function saveView() {
    try {
      setProcessing(true);
      await handleSaveView(nextViewSettings, nextView?.uuid);
      showToastWithTimeout("View has been saved successfully.", "success");
    } finally {
      setProcessing(false);
    }
  }

  function setDefaultViewFlag() {
    const displaySettings = nextView.displaySettings ?? {};

    return setNextView({
      ...nextView,
      displaySettings: {
        ...displaySettings,
        isDefaultView: !displaySettings.isDefaultView,
      },
    });
  }

  // splitBy, limit, splitBySortDirection keys
  function setViewSettingsByKey(key, value) {
    const displaySettings = nextView.displaySettings ?? {};

    return setNextView({
      ...nextView,
      displaySettings: {
        ...displaySettings,
        [key]: value,
      },
    });
  }

  function setVisibilityViewUser(visibilityType, viewVisibilityGroupUuids) {
    const displaySettings = nextView.displaySettings ?? {};

    return setNextView({
      ...nextView,
      displaySettings: {
        ...displaySettings,
        viewVisibilityUserUuid: visibilityType ? user.uuid : null,
        viewVisibilityGroupUuids:
          visibilityType === visibilityTypes.GROUPS
            ? viewVisibilityGroupUuids ?? []
            : null,
      },
    });
  }

  function setDisplaySettings(settings) {
    setNextView({ ...nextView, displaySettings: settings });
  }

  function pinUnpinColumn(colId, pinned) {
    const displaySettings = nextView.displaySettings ?? {};
    const pinnedColumns = displaySettings.pinnedColumns ?? [];
    const exists = pinnedColumns.find((item) => item.colId === colId);

    let updatedPinnedColumns;

    if (exists?.pinned === pinned) {
      // Unpin if already pinned to the same side
      updatedPinnedColumns = pinnedColumns.filter(
        (item) => item.colId !== colId
      );
    } else if (exists) {
      // Update pin side
      updatedPinnedColumns = pinnedColumns.map((item) =>
        item.colId === colId ? { ...item, pinned } : item
      );
    } else {
      // Add new pinned column
      updatedPinnedColumns = [...pinnedColumns, { colId, pinned }];
    }

    setDisplaySettings({
      ...displaySettings,
      pinnedColumns: updatedPinnedColumns,
    });
  }

  return {
    nextColumns,
    setNextColumns,
    visibleColumns,
    setVisible,
    setInvisible,
    changeValue,
    setAllVisible,
    setAllInvisible,
    nextViewSettings,
    name,
    setName,
    disabledReason,
    saveView,
    processing,
    isDirty,
    filterApi,
    displaySettings: nextView.displaySettings ?? {},
    setDefaultViewFlag,
    setVisibilityViewUser,
    setNextView,
    setFilters,
    setDisplaySettings,
    setViewSettingsByKey,
    viewForm,
    pinUnpinColumn,
  };
}

function convertedColumns(viewState, allColumns) {
  // Map visible fields first
  const visibleColumns = assembleColumnsAndVisibleFields(
    viewState.visibleFields,
    allColumns
  ).map(({ column, visibleField }) => ({
    ...column,
    visible: true,
    aggregationType: visibleField.aggregationType,
    // Do not allow aggregated columns to be editable, implicitly.
    editable: visibleField.aggregationType ? false : column.editable,
  }));

  const remainingColumns = allColumns
    .filter((c) => !viewState.visibleFields.find((f) => f.colId === c.colId))
    .map((c) => ({ ...c, visible: false }));

  const orderedColumns = [...visibleColumns, ...remainingColumns];

  return orderedColumns;
}

/**
 * @param {VisibleField[]} visibleFields
 * @param {ColDef[]} colDefs
 * @param {Array<{dataSourceFieldUuid: string, name: string}>} queryFields
 * @returns {Array<{visibleField: VisibleField, column: ColDef}>}
 */
export function assembleColumnsAndVisibleFields(
  visibleFields,
  colDefs,
  queryFields = []
) {
  const columnsByName = keyBy(colDefs, "field");
  const queryFieldsByUuid = keyBy(queryFields, "dataSourceFieldUuid");

  return visibleFields
    .map((visibleField) => {
      // for some reason this can be undefined, we need to filter out undefined values
      const column = colDefs.find((column) => {
        if (column.colId === visibleField.colId) {
          // Column match.
          return true;
        }
        const queryField = queryFieldsByUuid[visibleField.colId];

        // Is the visibleField's uuid referencing the output data source field's
        // uuid rather than the available active table column's? Then map it if
        // a matched active table column is found with the same name.
        return queryField
          ? column.colId === columnsByName[queryField.name]?.colId
          : false;
      });

      return {
        visibleField,
        column,
      };
    })
    .filter((c) => c.column);
}

/**
 * Assembles a list that contains the visible fields, along with minimal
 * column data coming from a combination of the active table columns and
 * the data source fields.
 *
 * @param {VisibleField[]} visibleFields
 * @param {Array<{uuid: string, name: string}>} columns
 * @param {Array<{dataSourceFieldUuid: string, name: string}>} queryFields
 * @returns {Array<{visibleField: VisibleField, column: {name: string}}>}
 */
export function assembleVisibleFieldsWithColumns(
  visibleFields,
  columns,
  queryFields = []
) {
  const columnsByUuid = keyBy(columns, "uuid");
  const queryFieldsByUuid = keyBy(queryFields, "dataSourceFieldUuid");

  return visibleFields.map((visibleField) => {
    const column = columnsByUuid[visibleField.colId]
      ? {
          name: columnsByUuid[visibleField.colId].name,
        }
      : queryFieldsByUuid[visibleField.colId]
      ? {
          name: queryFieldsByUuid[visibleField.colId].name,
        }
      : null;

    return {
      visibleField,
      column,
    };
  });
}

// visibleFields has cellEditorParams for image types
// it include handleChange function which changes to new instanse on update
// we should skeep this comparison
function isEqualComparator(a, b) {
  if (a instanceof Function && b instanceof Function) {
    return true;
  }
}
